import type { SituationStage } from "../hooks/useGameplans";

/**
 * Horizontal stage progression indicator. Renders one chip per stage
 * with colors matching the stage's `state` JSONB plus the
 * `currentIndex` highlight. Returns null when `stages` is empty so
 * callers can drop it in without wrapping conditionals.
 */
export function SituationStageBar({
  stages,
  currentIndex,
}: {
  stages: SituationStage[];
  currentIndex: number;
}) {
  if (!stages || stages.length === 0) return null;
  return (
    <div
      data-testid="situation-stage-bar"
      style={{
        display: "flex",
        gap: 6,
        alignItems: "stretch",
        padding: "10px 0",
        flexWrap: "wrap",
      }}
    >
      {stages.map((s, i) => {
        const isCurrent = i === currentIndex;
        const isCompleted = i < currentIndex;
        const bg = bgFor(s.state, isCurrent, isCompleted);
        const fg = fgFor(s.state, isCurrent);
        const border = borderFor(s.state, isCurrent);
        return (
          <div
            key={i}
            data-testid={`situation-stage-${i}`}
            data-current={isCurrent ? "true" : "false"}
            data-state={s.state}
            style={{
              flex: "1 1 0",
              minWidth: 90,
              padding: "8px 10px",
              borderRadius: 8,
              background: bg,
              color: fg,
              border: `1.5px solid ${border}`,
              fontSize: 12,
              fontWeight: isCurrent ? 700 : 500,
              textAlign: "center",
              transition: "background .15s, border-color .15s",
            }}
          >
            <div
              style={{
                fontSize: 10,
                opacity: 0.7,
                marginBottom: 2,
                letterSpacing: 0.3,
                textTransform: "uppercase",
              }}
            >
              Stage {i + 1}
            </div>
            <div>{s.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function bgFor(state: SituationStage["state"], current: boolean, completed: boolean) {
  if (state === "resolved") return "#dcfce7";
  if (state === "confirmed") return current ? "#fee2e2" : "#fef2f2";
  if (state === "suspected") return current ? "#fef3c7" : "#fefce8";
  if (completed) return "#f1f5f9";
  return "#f8fafc";
}
function fgFor(state: SituationStage["state"], current: boolean) {
  if (state === "resolved") return "#166534";
  if (state === "confirmed") return current ? "#991b1b" : "#7f1d1d";
  if (state === "suspected") return current ? "#854d0e" : "#713f12";
  return current ? "#0f172a" : "#475569";
}
function borderFor(state: SituationStage["state"], current: boolean) {
  if (state === "resolved") return "#86efac";
  if (state === "confirmed") return current ? "#dc2626" : "#fca5a5";
  if (state === "suspected") return current ? "#ca8a04" : "#fde68a";
  return current ? "#94a3b8" : "#e2e8f0";
}
